/**
 * Button color / intent — the ink axis.
 *
 * Solid stroke uses `.border-1.5` (inset shadow). Dashed uses a real CSS
 * border (`BUTTON_STROKE_DASHED`, 2px) because inset shadow cannot dash.
 * Literals must stay complete so Tailwind can detect them.
 * Density lives in Button.size.ts.
 */

import type { ButtonIntent, ButtonType } from './Button.types';

export const BUTTON_STROKE = 'border-1.5';

/**
 * Dashed cannot use inset box-shadow. Real 2px CSS dash instead.
 * `border-current` ties the ring to glyph/label `color` so they cannot diverge.
 * Important beats matched-fill (`bg` + `--inv-stroke` + `text-*`).
 */
export const BUTTON_STROKE_DASHED =
  '!border-[2px] !border-dashed !border-current !bg-transparent ![box-shadow:none]';

/** Intent ink for dashed — one token for dash, icon, and label (add-zone). */
export const buttonDashInk = {
  primary: '!text-purple disabled:!text-purple/38 !after:bg-purple',
  secondary: '!text-dark-invirtus disabled:!text-dark-invirtus/38 !after:bg-dark-invirtus',
  light: '!text-dark-invirtus/60 disabled:!text-dark-invirtus/38 !after:bg-dark-invirtus',
  muted: '!text-dark-invirtus/40 disabled:!text-dark-invirtus/38 !after:bg-dark-invirtus',
  success: '!text-green disabled:!text-green/38 !after:bg-green',
  danger: '!text-red disabled:!text-red/38 !after:bg-red',
  warning: '!text-yellow disabled:!text-yellow/38 !after:bg-yellow',
  info: '!text-blue disabled:!text-blue/38 !after:bg-blue',
  'success-soft': '!text-green disabled:!text-green/38 !after:bg-green',
  'danger-soft': '!text-red disabled:!text-red/38 !after:bg-red',
} as const;

/** Paint fill + stroke from the same opaque token. */
export const BUTTON_MATCHED_FILL =
  'bg-[var(--inv-fill)] [--inv-stroke:var(--inv-fill)]';

/** Opaque fills — used by contained + filled icon. Disabled = 12% on light. */
export const buttonFill = {
  purple:
    '[--inv-fill:var(--color-purple)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-purple)_12%,var(--color-light))]',
  purple25:
    '[--inv-fill:color-mix(in_srgb,var(--color-purple)_25%,var(--color-light))] disabled:[--inv-fill:color-mix(in_srgb,var(--color-purple)_12%,var(--color-light))]',
  noir:
    '[--inv-fill:var(--color-dark-invirtus)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-dark-invirtus)_12%,var(--color-light))]',
  light:
    '[--inv-fill:var(--color-light)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-light)_12%,var(--color-light))]',
  green:
    '[--inv-fill:var(--color-green)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-green)_12%,var(--color-light))]',
  green15:
    '[--inv-fill:color-mix(in_srgb,var(--color-green)_15%,var(--color-light))] disabled:[--inv-fill:color-mix(in_srgb,var(--color-green)_12%,var(--color-light))]',
  red:
    '[--inv-fill:var(--color-red)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-red)_12%,var(--color-light))]',
  red15:
    '[--inv-fill:color-mix(in_srgb,var(--color-red)_15%,var(--color-light))] disabled:[--inv-fill:color-mix(in_srgb,var(--color-red)_12%,var(--color-light))]',
  yellow:
    '[--inv-fill:var(--color-yellow)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-yellow)_12%,var(--color-light))]',
  yellow30:
    '[--inv-fill:color-mix(in_srgb,var(--color-yellow)_30%,var(--color-light))] disabled:[--inv-fill:color-mix(in_srgb,var(--color-yellow)_12%,var(--color-light))]',
  blue:
    '[--inv-fill:var(--color-blue)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-blue)_12%,var(--color-light))]',
  blue25:
    '[--inv-fill:color-mix(in_srgb,var(--color-blue)_25%,var(--color-light))] disabled:[--inv-fill:color-mix(in_srgb,var(--color-blue)_12%,var(--color-light))]',
  noirSoft:
    '[--inv-fill:var(--color-dark-invirtus-soft)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-dark-invirtus-soft)_12%,var(--color-light))]',
  greenSoft:
    '[--inv-fill:var(--color-green-soft)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-green-soft)_12%,var(--color-light))]',
  redSoft:
    '[--inv-fill:var(--color-red-soft)] disabled:[--inv-fill:color-mix(in_srgb,var(--color-red-soft)_12%,var(--color-light))]',
} as const;

/** Outlined ink only — must stay visible and distinct from the fill. */
export const buttonStroke = {
  purple:
    '[--inv-stroke:var(--color-purple)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-purple)_12%,transparent)]',
  noir:
    '[--inv-stroke:var(--color-dark-invirtus)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-dark-invirtus)_12%,transparent)]',
  noir10:
    '[--inv-stroke:color-mix(in_srgb,var(--color-dark-invirtus)_10%,transparent)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-dark-invirtus)_12%,transparent)]',
  noir20:
    '[--inv-stroke:color-mix(in_srgb,var(--color-dark-invirtus)_20%,transparent)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-dark-invirtus)_12%,transparent)]',
  green:
    '[--inv-stroke:var(--color-green)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-green)_12%,transparent)]',
  red:
    '[--inv-stroke:var(--color-red)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-red)_12%,transparent)]',
  yellow:
    '[--inv-stroke:var(--color-yellow)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-yellow)_12%,transparent)]',
  blue:
    '[--inv-stroke:var(--color-blue)] disabled:[--inv-stroke:color-mix(in_srgb,var(--color-blue)_12%,transparent)]',
  ghost: '[--inv-stroke:transparent]',
} as const;

/** Outer elevation layered with the inset stroke (`--inv-elevation`). */
export const BUTTON_ELEVATION =
  '[--inv-elevation:var(--shadow-card)] hover:[--inv-elevation:var(--shadow-popover)] ' +
  'active:[--inv-elevation:var(--shadow-card)] focus-visible:[--inv-elevation:var(--shadow-popover)]';

/**
 * M3 state-layer scrim on ::after — opacities: hover 8%, focus/pressed 12%.
 * Ink color (`after:bg-*`) is set per intent/type in INTENT_COLORS.
 */
export const STATE_LAYER =
  'isolate ' +
  'after:absolute after:inset-0 after:z-[1] after:rounded-[inherit] after:content-[""] ' +
  'after:pointer-events-none after:opacity-0 after:transition-opacity after:duration-200 ' +
  'hover:after:opacity-[0.08] focus-visible:after:opacity-[0.12] active:after:opacity-[0.12] ' +
  'disabled:after:opacity-0 aria-disabled:after:opacity-0';

/** Ring + press scale. Ring color comes from INTENT_COLORS (`focus-visible:ring-*`). */
export const INTERACTION_BASE =
  'outline-none focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.98] ' +
  'disabled:cursor-not-allowed disabled:active:scale-100 ' +
  'disabled:pointer-events-none aria-disabled:cursor-not-allowed';

type IntentMap = Record<ButtonType, string>;

const fill = buttonFill;
const stroke = buttonStroke;

/**
 * Container + ink + state-layer ink. Hover/active do NOT alpha-cut the
 * container fill — the ::after scrim carries interaction feedback.
 * Disabled: M3 two-channel — container /12, content /38.
 */
export const INTENT_COLORS: Record<ButtonIntent, IntentMap> = {
  primary: {
    contained:
      `${fill.purple} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-purple/40',
    outlined:
      `${stroke.purple} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-purple focus-visible:ring-purple/30',
    text:
      `${stroke.ghost} text-purple disabled:text-purple/38 bg-transparent after:bg-purple ` +
      'focus-visible:ring-purple/20',
    icon:
      `${fill.purple25} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-purple/40',
  },
  secondary: {
    contained:
      `${fill.noir} ${BUTTON_MATCHED_FILL} text-light disabled:text-light/38 ` +
      'after:bg-light focus-visible:ring-dark-invirtus/40',
    outlined:
      `${stroke.noir} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-dark-invirtus focus-visible:ring-dark-invirtus/30',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-dark-invirtus ` +
      'focus-visible:ring-dark-invirtus/20',
    icon:
      `${fill.noir} ${BUTTON_MATCHED_FILL} text-light disabled:text-light/38 ` +
      'after:bg-light focus-visible:ring-dark-invirtus/40',
  },
  light: {
    contained:
      `${fill.light} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      `${BUTTON_ELEVATION} after:bg-dark-invirtus focus-visible:ring-dark-invirtus/20`,
    outlined:
      `bg-light disabled:bg-light/12 text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      `${stroke.noir10} ${BUTTON_ELEVATION} ` +
      'after:bg-dark-invirtus focus-visible:ring-dark-invirtus/20',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-dark-invirtus ` +
      'focus-visible:ring-dark-invirtus/15',
    icon:
      `${fill.light} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      `${BUTTON_ELEVATION} after:bg-dark-invirtus focus-visible:ring-dark-invirtus/20`,
  },
  success: {
    contained:
      `${fill.green} ${BUTTON_MATCHED_FILL} text-light disabled:text-light/38 ` +
      'after:bg-light focus-visible:ring-green/40',
    outlined:
      `${stroke.green} text-green disabled:text-green/38 bg-transparent ` +
      'after:bg-green focus-visible:ring-green/30',
    text:
      `${stroke.ghost} text-green disabled:text-green/38 bg-transparent after:bg-green focus-visible:ring-green/20`,
    icon:
      `${fill.green15} ${BUTTON_MATCHED_FILL} text-green disabled:text-green/38 ` +
      'after:bg-green focus-visible:ring-green/40',
  },
  danger: {
    contained:
      `${fill.red} ${BUTTON_MATCHED_FILL} text-light disabled:text-light/38 ` +
      'after:bg-light focus-visible:ring-red/40',
    outlined:
      `${stroke.red} text-red disabled:text-red/38 bg-transparent ` +
      'after:bg-red focus-visible:ring-red/30',
    text:
      `${stroke.ghost} text-red disabled:text-red/38 bg-transparent after:bg-red focus-visible:ring-red/20`,
    icon:
      `${fill.red15} ${BUTTON_MATCHED_FILL} text-red disabled:text-red/38 ` +
      'after:bg-red focus-visible:ring-red/40',
  },
  warning: {
    contained:
      `${fill.yellow} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-yellow/40',
    outlined:
      `${stroke.yellow} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-yellow focus-visible:ring-yellow/30',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-yellow ` +
      'focus-visible:ring-yellow/20',
    icon:
      `${fill.yellow30} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-yellow focus-visible:ring-yellow/40',
  },
  info: {
    contained:
      `${fill.blue} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-blue/40',
    outlined:
      `${stroke.blue} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-blue focus-visible:ring-blue/30',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-blue ` +
      'focus-visible:ring-blue/20',
    icon:
      `${fill.blue25} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-blue focus-visible:ring-blue/40',
  },
  muted: {
    contained:
      `${fill.noirSoft} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-dark-invirtus/20',
    outlined:
      `${stroke.noir20} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-dark-invirtus focus-visible:ring-dark-invirtus/20',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-dark-invirtus ` +
      'focus-visible:ring-dark-invirtus/20',
    icon:
      `${stroke.ghost} text-dark-invirtus/70 disabled:text-dark-invirtus/38 bg-transparent after:bg-dark-invirtus ` +
      'focus-visible:ring-dark-invirtus/20',
  },
  'success-soft': {
    contained:
      `${fill.greenSoft} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-green/30',
    outlined:
      `${stroke.green} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-green focus-visible:ring-green/20',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-green ` +
      'focus-visible:ring-green/20',
    icon:
      `${fill.greenSoft} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-green/30',
  },
  'danger-soft': {
    contained:
      `${fill.redSoft} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-red/30',
    outlined:
      `${stroke.red} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'bg-transparent after:bg-red focus-visible:ring-red/20',
    text:
      `${stroke.ghost} text-dark-invirtus disabled:text-dark-invirtus/38 bg-transparent after:bg-red ` +
      'focus-visible:ring-red/20',
    icon:
      `${fill.redSoft} ${BUTTON_MATCHED_FILL} text-dark-invirtus disabled:text-dark-invirtus/38 ` +
      'after:bg-dark-invirtus focus-visible:ring-red/30',
  },
};

/**
 * Selected resting container — sibling of INTENT_COLORS.
 * Driven by `data-selected="true"`. Does not change `type`.
 */
export const SELECTED_SURFACE: Record<ButtonIntent, IntentMap> = {
  primary: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-purple/30 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-purple/15',
    text: 'data-[selected=true]:bg-purple/12',
    icon: 'data-[selected=true]:bg-purple data-[selected=true]:text-dark-invirtus',
  },
  secondary: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-dark-invirtus/50 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-dark-invirtus/10',
    text: 'data-[selected=true]:bg-dark-invirtus/8',
    icon:
      'data-[selected=true]:bg-dark-invirtus-soft data-[selected=true]:text-dark-invirtus ' +
      'data-[selected=true]:after:bg-dark-invirtus',
  },
  light: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-dark-invirtus/25 data-[selected=true]:ring-offset-2 ' +
      'data-[selected=true]:shadow-sm',
    outlined:
      'data-[selected=true]:[--inv-stroke:color-mix(in_srgb,var(--color-dark-invirtus)_25%,transparent)] ' +
      'data-[selected=true]:bg-dark-invirtus/5',
    text: 'data-[selected=true]:bg-dark-invirtus/8',
    icon:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-dark-invirtus/25 data-[selected=true]:ring-offset-2 ' +
      'data-[selected=true]:shadow-sm',
  },
  success: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-green/50 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-green/15',
    text: 'data-[selected=true]:bg-green/12',
    icon: 'data-[selected=true]:bg-green data-[selected=true]:text-light',
  },
  danger: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-red/50 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-red/15',
    text: 'data-[selected=true]:bg-red/12',
    icon: 'data-[selected=true]:bg-red data-[selected=true]:text-light',
  },
  warning: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-yellow/60 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-yellow/25',
    text: 'data-[selected=true]:bg-yellow/20',
    icon: 'data-[selected=true]:bg-yellow data-[selected=true]:text-dark-invirtus',
  },
  info: {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-blue/50 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-blue/15',
    text: 'data-[selected=true]:bg-blue/12',
    icon: 'data-[selected=true]:bg-blue data-[selected=true]:text-dark-invirtus',
  },
  muted: {
    contained: 'data-[selected=true]:bg-dark-invirtus/15',
    outlined: 'data-[selected=true]:bg-dark-invirtus/10',
    text: 'data-[selected=true]:bg-dark-invirtus/8',
    icon: 'data-[selected=true]:bg-dark-invirtus/10 data-[selected=true]:text-dark-invirtus',
  },
  'success-soft': {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-green/40 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-green-soft',
    text: 'data-[selected=true]:bg-green-soft',
    icon:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-green/40 data-[selected=true]:ring-offset-2',
  },
  'danger-soft': {
    contained:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-red/40 data-[selected=true]:ring-offset-2',
    outlined: 'data-[selected=true]:bg-red-soft',
    text: 'data-[selected=true]:bg-red-soft',
    icon:
      'data-[selected=true]:ring-2 data-[selected=true]:ring-red/40 data-[selected=true]:ring-offset-2',
  },
};
